import Requests from "@/services/Requests";

const state = {
  mainProjects: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  createStatus: "unitiliazed", // unitiliazed, loading, success, error
  updateStatus: "unitiliazed", // unitiliazed, loading, success, error
  deleteStatus: "unitiliazed", // unitiliazed, loading, success, error
  mainProjectById: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  mainProjectChangedLogs: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  mainProjectPermissions: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
};
const getters = {
  /**
   * Returns a subset of projects based on the given page number.
   *
   * @param {Object} state - The Vuex state object.
   * @param {number} [page=0] - The page number to filter projects by. Defaults to 0.
   * @return {Array} An array of projects filtered by the given page number. If page is 0, returns all projects.
   */
  getMainProjects:
    (state) =>
    (page = 0) =>
      page === 0
        ? state.mainProjects.data
        : state.mainProjects.data
            .filter(
              (value, index, self) =>
                index === self.findIndex((t) => t.id === value.id)
            )
            .filter((d) => d.page === page),
  getMainProjectStatus: (state) => state.mainProjects.status,
  getMainProjectCount: (state) => state.mainProjects.count,
  getMainProjectPage: (state) => state.mainProjects.page,
  getMainProjectPages: (state) => Math.ceil(state.mainProjects.count / 10),
  getMainProjectsLoading: (state) => state.mainProjects.status === "loading",
  getMainProjectById: (state) => (id) =>
    state.mainProjectById.data.find((d) => d.id === id) || {},
  getMainProjectChangedLogsById: (state) => (id) =>
    state.mainProjectChangedLogs.data.find((d) => d.projectId === id) || {
      items: [],
    },
  getMainProjectChangedLogsLoadingById: (state) =>
    state.mainProjectChangedLogs.status === "loading",
  getMainProjectPermissionsById: (state) => (id) =>
    state.mainProjectPermissions.data.find((d) => d.projectId === id) || {
      items: [],
    },
  getMainProjectPermissionsLoadingById: (state) =>
    state.mainProjectPermissions.status === "loading",
};
const mutations = {
  /**
   * Updates the status of the project in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the project.
   */
  SET_MAIN_PROJECT_STATUS(state, payload) {
    state.mainProjects.status = payload;
  },
  /**
   * Sets the projects and count in the state based on the payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the items and count.
   * @param {Array} payload.items - The array of projects.
   * @param {number} payload.count - The total count of projects.
   */
  SET_MAIN_PROJECTS(state, payload) {
    state.mainProjects.data = payload.items;
    state.mainProjects.count = payload.count;
  },
  /**
   * Sets the projects and count in the state based on the payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the items and count.
   * @param {Array} payload.items - The array of projects.
   * @param {number} payload.count - The total count of projects.
   */
  SET_ALL_MAIN_PROJECTS(state, payload) {
    state.allMainProjects.data = payload.items;
    state.allMainProjects.count = payload.count;
  },
  /**
   * Updates the status of the update project in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the project.
   */
  SET_UPDATE_MAIN_PROJECT_STATUS(state, payload) {
    state.updateStatus = payload;
  },
  /**
   * Updates an project object in the state with new information.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The updated project object.
   * @return {void}
   */
  SET_UPDATE_MAIN_PROJECT(state, payload) {
    const index = state.mainProjects.data.findIndex((d) => d.id === payload.id);
    const project = state.mainProjects.data[index];
    state.mainProjects.data[index] = { ...project, ...payload };
  },
  SET_CREATE_MAIN_PROJECT_STATUS(state, payload) {
    state.createStatus = payload;
  },
  /**
   * Adds a new project to the list of projects in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The project object to be added.
   * @return {void}
   */
  SET_CREATE_MAIN_PROJECT(state, payload) {
    state.mainProjects.data.push(payload);
  },
  /**
   * Updates the status of the delete project in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the project.
   */
  SET_DELETE_MAIN_PROJECT_STATUS(state, payload) {
    state.deleteStatus = payload;
  },
  /**
   * Removes an project from the state based on the provided payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the project ID to be deleted.
   * @param {number} payload.id - The ID of the project to be deleted.
   * @return {void}
   */
  SET_DELETE_MAIN_PROJECT(state, payload) {
    state.mainProjects.data = state.mainProjects.data.filter(
      (d) => d.id !== payload.id
    );
  },
  SET_MAIN_PROJECT_BY_ID_STATUS(state, payload) {
    state.mainProjectById.status = payload;
  },
  SET_MAIN_PROJECT_BY_ID(state, payload) {
    const index = state.mainProjectById.data.findIndex(
      (d) => d.id === payload.id
    );
    if (index > -1) state.mainProjectById.data.splice(index, 1);
    state.mainProjectById.data.push(payload);
  },
  SET_MAIN_PROJECT_CHANGED_LOGS_BY_ID_STATUS(state, payload) {
    state.mainProjectChangedLogs.status = payload;
  },
  SET_MAIN_PROJECT_CHANGED_LOGS_BY_ID(state, payload) {
    const index = state.mainProjectChangedLogs.data.findIndex(
      (d) => d.projectId === payload.projectId
    );
    if (index > -1) state.mainProjectChangedLogs.data.splice(index, 1);
    state.mainProjectChangedLogs.data.unshift(payload);
  },
  SET_MAIN_PROJECT_PERMISSIONS_BY_ID_STATUS(state, payload) {
    state.mainProjectPermissions.status = payload;
  },
  SET_MAIN_PROJECT_PERMISSIONS_BY_ID(state, payload) {
    const index = state.mainProjectPermissions.data.findIndex(
      (d) => d.projectId === payload.projectId
    );
    if (index > -1) state.mainProjectPermissions.data.splice(index, 1);
    state.mainProjectPermissions.data.unshift(payload);
  },
};

const actions = {
  /**
   * Retrieves a list of projects based on the provided payload.
   *
   * @param {Object} commit - The Vuex commit function.
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the query parameters.
   * @param {Object} payload.query - The query parameters.
   * @param {string} payload.query.paymentMethodId - The ID of the payment method to filter by.
   * @return {Promise<void>}
   */
  async getMainProjects({ commit }, payload) {
    commit("SET_MAIN_PROJECT_STATUS", "loading");
    const response = await Requests.MainProject.GetAll(payload);
    if (response.message === "OK") {
      commit("SET_MAIN_PROJECTS", {
        items: response.data.items.map((item) => ({
          ...item,
          page: payload.query.page || 1,
        })),
        count: response.data.count,
      });
    }
    commit("SET_MAIN_PROJECT_STATUS", response.message === "OK" ? "success" : response.message);
    return response.message;
  },
  /**
   * Creates an project by making a request to the server.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the project data.
   * @param {string} payload.paymentMethodId - The ID of the payment method.
   * @param {string} payload.projectRate - The project rate.
   * @return {Promise<void>} - A promise that resolves when the creation is complete.
   */
  async createMainProject({ state, commit }, payload) {
    state.createStatus = "loading";
    try {
      const response = await Requests.MainProject.Create(payload);
      state.createStatus = "success";
      if (response.message === "OK")
        commit("SET_CREATE_MAIN_PROJECT", {
          id: response.data,
          ...payload,
          isActive: true,
          page: 1,
        });
      else state.createStatus = "error";
      return response;
    } catch {
      state.createStatus = "error";
      return { message: "ERROR" };
    }
  },
  /**
   * Updates an project by making a request to the server.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the project data to update.
   * @param {string} payload.paymentMethodId - The ID of the payment method.
   * @param {string} payload.projectRate - The project rate.
   * @param {boolean} payload.isActive - Whether the project is active or not.
   * @return {Promise<void>} - A promise that resolves when the update is complete.
   */
  async updateMainProject({ state, commit }, payload) {
    state.updateStatus = "loading";
    try {
      const response = await Requests.MainProject.Update(payload, {
        params: { id: payload.id },
      });
      if (response.message === "OK") {
        commit("SET_UPDATE_MAIN_PROJECT", payload);
        state.updateStatus = "success";
      } else {
        state.updateStatus = "error";
      }
      return response;
    } catch {
      state.updateStatus = "error";
      return { message: "ERROR" };
    }
  },
  /**
   * Removes an project from the state and updates the delete status.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the project data.
   * @param {number} payload.id - The ID of the project to be removed.
   * @returns {Promise<void>} A promise that resolves when the project is successfully removed.
   */
  removeMainProject({ state }, { id }) {
    state.deleteStatus = "loading";

    return Requests.MainProject.Delete({ params: { id } })
      .then((response) => {
        state.deleteStatus = response.message === "OK" ? "success" : "error";
        return response;
      })
      .catch(() => {
        state.deleteStatus = "error";
        return { message: "ERROR" };
      });
  },
  /**
   * Retrieves an project by their ID and updates the project by ID status in the Vuex store.
   *
   * @param {Object} commit - The Vuex commit function.
   * @param {Object} id - The ID of the project to retrieve.
   * @return {Promise} A promise that resolves when the project is successfully retrieved and the status is updated.
   */
  getMainProjectById({ commit, state }, { id, force = false }) {
    // check if project already exists in the state;
    console.log(id, force, "store");

    const project = state.mainProjectById.data.find(
      (project) => project.id == id
    );
    if (project && !force) return { message: "OK", data: project };
    commit("SET_MAIN_PROJECT_BY_ID_STATUS", "loading");
    return Requests.MainProject.GetById({ params: { id } })
      .then((response) => {
        if (response.message === "OK")
          if (response.data) commit("SET_MAIN_PROJECT_BY_ID", response.data);
        commit("SET_MAIN_PROJECT_BY_ID_STATUS", "success");
        return response;
      })
      .catch(() => {
        commit("SET_MAIN_PROJECT_BY_ID_STATUS", "error");
        return { message: "FAIL" };
      });
  },
};

export default { state, getters, mutations, actions };
